import { useEffect, useMemo, useRef, useState } from 'react'
import { Select } from 'antd'
import { Label } from 'components/ui/label'
import { CaretSortIcon } from '@radix-ui/react-icons'
import { getIn, useFormikContext } from 'formik'
import { twMerge } from 'tailwind-merge'

export default function AsyncSearchSelect({
  name,
  label,
  labelName,
  className,
  value,
  fetchOptions,
  fetchSelected,
  onChange,
  placeholder = 'Ara...',
  debounceMs = 300,
  disabled,
  allowClear = true,
}) {
  const { values, setFieldValue } = useFormikContext()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [loading, setLoading] = useState(false)
  const fetchOptionsRef = useRef(fetchOptions)
  const fetchSelectedRef = useRef(fetchSelected)
  const requestId = useRef(0)
  fetchOptionsRef.current = fetchOptions
  fetchSelectedRef.current = fetchSelected
  const formValue = getIn(values, name)
  const rawValue = formValue !== undefined && formValue !== null && formValue !== '' ? formValue : value
  const currentValue = rawValue === undefined || rawValue === null ? '' : String(rawValue)

  const cn = twMerge(`grid ${label ? 'grid-cols-[100px_1fr]' : ''} items-center`, className)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), debounceMs)
    return () => clearTimeout(timer)
  }, [search, debounceMs])

  useEffect(() => {
    let cancelled = false
    if (!currentValue) {
      setSelectedOption(null)
      return undefined
    }
    if (selectedOption && String(selectedOption.value) === currentValue) return undefined

    ;(async () => {
      if (!fetchSelectedRef.current) return
      const option = await fetchSelectedRef.current(currentValue)
      if (cancelled) return
      if (option?.value != null) setSelectedOption(option)
      else setSelectedOption({ value: currentValue, label: currentValue })
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue])

  useEffect(() => {
    if (!open) return undefined
    const id = ++requestId.current
    setLoading(true)
    Promise.resolve(fetchOptionsRef.current ? fetchOptionsRef.current(debouncedSearch) : [])
      .then((rows) => {
        if (id !== requestId.current) return
        setOptions(Array.isArray(rows) ? rows.filter((row) => row?.value != null && row.value !== '') : [])
      })
      .catch(() => {
        if (id !== requestId.current) return
        setOptions([])
      })
      .finally(() => {
        if (id !== requestId.current) return
        setLoading(false)
      })
    return undefined
  }, [open, debouncedSearch])

  const mergedOptions = useMemo(() => {
    const map = new Map()
    if (selectedOption?.value != null) map.set(String(selectedOption.value), selectedOption)
    for (const option of options) {
      map.set(String(option.value), option)
    }
    return [...map.values()]
  }, [options, selectedOption])

  const commit = (option) => {
    const next = option?.value != null ? option : null
    setSelectedOption(next)
    setFieldValue(name, next ? next.value : '')
    if (labelName) setFieldValue(labelName, next ? next.label : '')
    if (onChange) onChange(next)
  }

  return (
    <div className={cn} style={{ maxWidth: '100%' }}>
      {label && (
        <Label className="text-simsek" htmlFor={name}>
          {label}
        </Label>
      )}
      <Select
        id={name}
        showSearch
        allowClear={allowClear}
        disabled={disabled}
        suffixIcon={<CaretSortIcon />}
        className="h-10"
        placeholder={placeholder}
        filterOption={false}
        defaultActiveFirstOption={false}
        notFoundContent={loading ? 'Yükleniyor...' : 'Kayıt yok'}
        loading={loading}
        options={mergedOptions}
        value={currentValue || undefined}
        onSearch={(text) => {
          setOpen(true)
          setSearch(text)
        }}
        onDropdownVisibleChange={(nextOpen) => {
          setOpen(nextOpen)
          if (nextOpen && !search) setDebouncedSearch('')
          if (!nextOpen) setSearch('')
        }}
        onClear={() => commit(null)}
        onSelect={(selectedValue, option) => {
          commit({
            value: selectedValue,
            label: option?.label ?? selectedValue,
          })
        }}
      />
    </div>
  )
}
